/**
 * Compliance Document Ledger — IN-1.b
 * ───────────────────────────────────
 * Append-only document evidence layer for contractor compliance.
 *
 * IN-1.b is **observation-only**:
 *   • no UI consumers required
 *   • no dispatch enforcement
 *   • no vetting promotion rewrite
 *   • no payout coupling
 *   • no auto-suspension
 *
 * Soft enforcement remains the locked default. Document state is
 * surfaced via `getComplianceStatus()` (see `./complianceStatus.ts`)
 * for diagnostic / advisory consumption only.
 *
 * Hard rules:
 *   • append-only — no destructive delete
 *   • supersede via `supersededBy` chain — old rows stay in history
 *   • rejected docs stay in history
 *   • every writer requires an explicit `actor`
 *   • rejection requires a `reason`
 *
 * Anchor: RECONCILIATION.md §13 (IN-1.b)
 */

export type ComplianceDocumentType =
  | "license"
  | "insurance"
  | "w9"
  | "background_check"
  | "agreement"
  | "certification"
  | "banking_verification"
  | "other";

export interface ComplianceDocument {
  id: string;
  contractorId: string;
  type: ComplianceDocumentType;
  url?: string;
  fileName?: string;
  uploadedAt: string;
  uploadedBy: string;
  expiresAt?: string;
  verifiedAt?: string;
  verifiedBy?: string;
  rejectedAt?: string;
  rejectedBy?: string;
  rejectionReason?: string;
  /** ID of the newer document that replaces this one. */
  supersededBy?: string;
  metadata?: Record<string, unknown>;
}

export interface SubmitComplianceDocumentInput {
  contractorId: string;
  type: ComplianceDocumentType;
  url?: string;
  fileName?: string;
  expiresAt?: string;
  metadata?: Record<string, unknown>;
}

// ─────────────────────────────────────────────────────────────────────
// ID generation (deterministic-ish, stable across calls within a tick)
// ─────────────────────────────────────────────────────────────────────

let __counter = 0;
function nextId(prefix: string): string {
  __counter += 1;
  return `${prefix}-${Date.now().toString(36)}-${__counter.toString(36)}`;
}

function nowIso(): string {
  return new Date().toISOString();
}

function requireNonEmpty(value: string | undefined, field: string): string {
  const v = (value ?? "").trim();
  if (!v) {
    throw new Error(`complianceDocuments: ${field} is required.`);
  }
  return v;
}

// ─────────────────────────────────────────────────────────────────────
// Writers (pure — return new ledger arrays, never mutate the input)
// ─────────────────────────────────────────────────────────────────────

/**
 * Append a new compliance document to the ledger.
 * Pure: returns a new array; does not mutate `ledger`.
 */
export function submitComplianceDocument(
  ledger: ComplianceDocument[],
  input: SubmitComplianceDocumentInput,
  actor: string,
): { ledger: ComplianceDocument[]; document: ComplianceDocument } {
  const uploadedBy = requireNonEmpty(actor, "actor");
  const contractorId = requireNonEmpty(input.contractorId, "contractorId");
  if (!input.type) {
    throw new Error("complianceDocuments: type is required.");
  }
  const doc: ComplianceDocument = {
    id: nextId("cdoc"),
    contractorId,
    type: input.type,
    url: input.url,
    fileName: input.fileName,
    uploadedAt: nowIso(),
    uploadedBy,
    expiresAt: input.expiresAt,
    metadata: input.metadata,
  };
  return { ledger: [...ledger, doc], document: doc };
}

/**
 * Mark a document as verified. Idempotent: re-verifying an already
 * verified doc is a no-op (returns the same ledger reference).
 */
export function verifyComplianceDocument(
  ledger: ComplianceDocument[],
  documentId: string,
  actor: string,
): ComplianceDocument[] {
  const verifiedBy = requireNonEmpty(actor, "actor");
  const target = ledger.find((d) => d.id === documentId);
  if (!target) {
    throw new Error(`complianceDocuments: document ${documentId} not found.`);
  }
  if (target.rejectedAt) {
    throw new Error(
      `complianceDocuments: document ${documentId} was rejected; cannot verify.`,
    );
  }
  if (target.verifiedAt) {
    return ledger;
  }
  return ledger.map((d) =>
    d.id === documentId ? { ...d, verifiedAt: nowIso(), verifiedBy } : d,
  );
}

/**
 * Mark a document as rejected. Requires a non-empty reason.
 * Rejected docs stay in the ledger (history).
 */
export function rejectComplianceDocument(
  ledger: ComplianceDocument[],
  documentId: string,
  actor: string,
  reason: string,
): ComplianceDocument[] {
  const rejectedBy = requireNonEmpty(actor, "actor");
  const rejectionReason = requireNonEmpty(reason, "reason");
  const target = ledger.find((d) => d.id === documentId);
  if (!target) {
    throw new Error(`complianceDocuments: document ${documentId} not found.`);
  }
  if (target.verifiedAt) {
    throw new Error(
      `complianceDocuments: document ${documentId} already verified; cannot reject.`,
    );
  }
  if (target.rejectedAt) {
    return ledger;
  }
  return ledger.map((d) =>
    d.id === documentId
      ? { ...d, rejectedAt: nowIso(), rejectedBy, rejectionReason }
      : d,
  );
}

/**
 * Replace an existing document with a new submission. Both docs stay
 * in the ledger; the old one is marked `supersededBy` the new doc id.
 */
export function supersedeComplianceDocument(
  ledger: ComplianceDocument[],
  oldDocumentId: string,
  newDocumentInput: SubmitComplianceDocumentInput,
  actor: string,
): { ledger: ComplianceDocument[]; document: ComplianceDocument } {
  const old = ledger.find((d) => d.id === oldDocumentId);
  if (!old) {
    throw new Error(
      `complianceDocuments: document ${oldDocumentId} not found.`,
    );
  }
  if (old.supersededBy) {
    throw new Error(
      `complianceDocuments: document ${oldDocumentId} already superseded.`,
    );
  }
  const { ledger: appended, document } = submitComplianceDocument(
    ledger,
    newDocumentInput,
    actor,
  );
  const linked = appended.map((d) =>
    d.id === oldDocumentId ? { ...d, supersededBy: document.id } : d,
  );
  return { ledger: linked, document };
}

// ─────────────────────────────────────────────────────────────────────
// Read helpers
// ─────────────────────────────────────────────────────────────────────

export function getDocumentsForContractor(
  ledger: ComplianceDocument[],
  contractorId: string,
): ComplianceDocument[] {
  return ledger.filter((d) => d.contractorId === contractorId);
}

export function isActiveDocument(doc: ComplianceDocument): boolean {
  return !doc.supersededBy && !doc.rejectedAt;
}
